import Dialog from '@mui/material/Dialog';
import { Box, Button, Typography, useTheme, Stack } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { FormButton } from 'theme/elements/Buttons';
import { useAppSelector } from 'hooks/redux.hooks';
import { selectLanguage } from 'state/reducers/translation.reducers';
import { useMemo } from 'react';
import _ from 'lodash';

type ButtonConfig = {
	label: string;
	onClick: () => void;
	variant?: 'contained' | 'outlined' | 'text';
	autoFocus?: boolean;
	disabled?: boolean;
};

type ImagePopupProps = {
	open: boolean;
	image: string;
	title: string;
	subtitle?: string;
	buttons: ButtonConfig[];
};

export default function ImagePopup(props: ImagePopupProps) {
	const theme = useTheme();
	const lang = useAppSelector(selectLanguage);
	const dir = useMemo(() => (_.includes(lang, 'he') ? 'rtl' : 'ltr'), [lang]);

	return (
		<Dialog
			open={props.open}
			aria-labelledby="image-popup-title"
			aria-describedby="image-popup-description"
			maxWidth="xs"
			slotProps={{
				backdrop: {
					style: {
						backgroundColor: 'rgba(0, 0, 0, 0.85)',
					},
				},
			}}
			sx={{
				'& .MuiPaper-root': {
					margin: '8px',
					maxWidth: '360px',
					width: '100%',
					direction: dir,
					[theme.breakpoints.down('sm')]: {
						margin: '16px',
						maxWidth: '320px',
						width: '90%',
					},
				},
			}}
		>
			<Box sx={{ textAlign: 'center', padding: '12px 16px 24px 16px', direction: dir }}>
				<Stack spacing={0}>
					{/* Image */}
					<Box
						sx={{
							width: '120px',
							height: '120px',
							margin: 'auto!important',
							marginTop: '16px!important',
							borderRadius: '12px',
							overflow: 'hidden',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							padding: '8px',
							boxSizing: 'border-box',
						}}
					>
						<img
							src={props.image}
							alt="popup"
							style={{
								width: '100%',
								height: '100%',
								objectFit: 'contain',
								objectPosition: 'center',
							}}
						/>
					</Box>

					{/* Title */}
					<Stack spacing={0} sx={{ padding: '0 16px' }}>
						<Typography
							variant="h3"
							sx={{
								padding: '0 8px',
								direction: dir,
								unicodeBidi: 'plaintext',
								[theme.breakpoints.up('sm')]: { padding: '0 16px' },
							}}
						>
							{props.title}
						</Typography>
						{props.subtitle ? (
							<Typography
								variant="body2"
								sx={{
									padding: '0 8px',
									marginTop: '8px',
									direction: dir,
									unicodeBidi: 'plaintext',
									whiteSpace: 'pre-line',
									[theme.breakpoints.up('sm')]: { padding: '0 16px' },
								}}
							>
								{props.subtitle}
							</Typography>
						) : null}
					</Stack>

					{/* Buttons */}
					<Stack gap={2} direction="column" justifyContent="center" alignItems="center" sx={{ marginTop: 4 }}>
						{props.buttons.map((button, index) => (
							<FormButton key={index}>
								<Button
									variant={button.variant || 'outlined'}
									onClick={button.onClick}
									autoFocus={button.autoFocus}
									disabled={button.disabled}
									sx={{ width: '280px' }}
								>
									{button.label}
								</Button>
							</FormButton>
						))}
					</Stack>
				</Stack>
			</Box>
		</Dialog>
	);
}
